// src/pages/Contact.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";
import { useMe } from "../contexts/MeContext";

const API = process.env.REACT_APP_API_BASE_URL || "";
const LINE_QR_URL =
  "https://qr-official.line.me/gs/M_020oicyf_GW.png?oat_content=qr";
const LINE_ADD_URL = "https://lin.ee/nEzir3E"; // 既存LPのLINEボタンURL

export default function Contact() {
  const navigate = useNavigate();
  const { me } = useMe();

  // 入力
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");

  // 付帯
  const [loading, setLoading] = useState(false);
  const [okId, setOkId] = useState<number | null>(null);
  const [err, setErr] = useState("");

  // MeContext からメールアドレスを初期セット（ユーザーが書き換えたら優先）
  useEffect(() => {
    if (!me) return;
    const meAny = me as any;
    const meEmail = meAny?.email || "";
    if (!meEmail) return;

    setEmail((prev) => (prev ? prev : meEmail));
  }, [me]);

  // ログイン中ユーザー名（Username）をそのまま使う
  // → MeContext は lineName を持っている前提で、name には頼らない
  const username = useMemo(() => {
    if (!me) return "";
    return me.lineName || "";
  }, [me]);

  const emailValid = useMemo(() => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email]);

  const textRemain = 1000 - text.length;

  const canSubmit = useMemo(() => {
    return (
      !loading &&
      !!username.trim() && // 必ずログイン名があること
      text.trim() !== "" &&
      emailValid &&
      text.length <= 1000
    );
  }, [loading, username, text, emailValid]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      setErr("");
      setOkId(null);

      // MeContext の型に userId / id どちらが入っていても拾えるようにする
      const meAny = me as any | null;
      const contactUserId =
        meAny?.userId !== undefined ? meAny.userId : meAny?.id ?? null;

      const body = {
        name: username.trim(), // Username をそのまま送る
        email: email.trim(),
        message: text.trim(), // 新仕様
        text: text.trim(), // 旧仕様との互換用
        userId: contactUserId, // 会員ID（userId or id）
      };

      const r = await fetch(`${API}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const b = await r.json().catch(() => ({}));
      if (!r.ok || (b as any)?.error) {
        throw new Error(
          (b as any)?.error || `送信に失敗しました（HTTP ${r.status}）`
        );
      }
      setOkId(Number((b as any).id) || null);
      setText("");
    } catch (e: any) {
      setErr(
        e?.message || "送信に失敗しました。時間をおいてお試しください。"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Header />
      <div
        style={{
          maxWidth: 720,
          margin: "20px auto",
          padding: "0 12px",
          minHeight: "70vh",
        }}
      >
        {okId ? (
          <div
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 12,
              border: "1px solid #e6edf7",
            }}
          >
            <h2 style={{ marginTop: 0 }}>お問い合わせを受け付けました</h2>
            <p>
              受付番号：<b>#{okId}</b>
            </p>
            <p>
              担当者より順次ご連絡いたします（差出人：
              <b>support@boat-okuman.com</b>）。返信までしばらくお待ちください。
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                onClick={() => navigate("/lp")}
                style={{
                  padding: "10px 16px",
                  background: "#1976d2",
                  color: "#fff",
                  borderRadius: 8,
                  border: 0,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                LPへ戻る
              </button>
              <button
                onClick={() => setOkId(null)}
                style={{
                  padding: "10px 16px",
                  background: "#f5f7fb",
                  color: "#1f2d3d",
                  borderRadius: 8,
                  border: "1px solid #d0dae6",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                もう一件送る
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 12,
              border: "1px solid #e6edf7",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <Link
                to="/lp"
                style={{ color: "#1976d2", textDecoration: "underline" }}
              >
                ← LPへ
              </Link>
              <h2 style={{ margin: 0 }}>お問い合わせ</h2>
            </div>

            <form
              onSubmit={submit}
              style={{ display: "grid", gap: 12 }}
              noValidate
            >
              {/* お名前（Username、自動セット・編集不可） */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 700,
                    marginBottom: 6,
                  }}
                >
                  お名前 <span style={{ color: "#e53935" }}>*</span>
                  <span
                    style={{
                      color: "#718096",
                      fontWeight: 400,
                      marginLeft: 6,
                      fontSize: 12,
                    }}
                  >
                    （ログイン中のユーザー名が自動で設定されます）
                  </span>
                </label>
                <input
                  value={username}
                  readOnly
                  placeholder="ログイン中のユーザー名"
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 10,
                    border: "1px solid #d0dae6",
                    outline: "none",
                    background: "#edf2f7",
                    color: "#2d3748",
                    fontWeight: 600,
                  }}
                />
                {!username && (
                  <div
                    style={{
                      color: "#e53935",
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    ユーザー名を取得できませんでした。再度ログインしてお試しください。
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 700,
                    marginBottom: 6,
                  }}
                >
                  メールアドレス{" "}
                  <span
                    style={{
                      color: "#718096",
                      fontWeight: 400,
                      marginLeft: 6,
                      fontSize: 12,
                    }}
                  >
                    （任意）
                  </span>
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@example.com"
                  autoComplete="email"
                  inputMode="email"
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 10,
                    border: `1px solid ${
                      emailValid ? "#d0dae6" : "#e53935"
                    }`,
                    outline: "none",
                    background: "#f7faff",
                  }}
                />
                {!emailValid && (
                  <div
                    style={{
                      color: "#e53935",
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    メールアドレスの形式が正しくありません。
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 700,
                    marginBottom: 6,
                  }}
                >
                  お問い合わせ内容{" "}
                  <span style={{ color: "#e53935" }}>*</span>
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={8}
                  maxLength={1000}
                  placeholder="できるだけ詳しくご記入ください。"
                  required
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 10,
                    border: "1px solid #d0dae6",
                    outline: "none",
                    background: "#f7faff",
                  }}
                />
                <div
                  style={{
                    textAlign: "right",
                    fontSize: 12,
                    color: textRemain < 0 ? "#e53935" : "#6b7b93",
                  }}
                >
                  残り {Math.max(0, textRemain)} 文字
                </div>
              </div>

              {err && (
                <div
                  style={{
                    color: "#e53935",
                    background: "#fff5f5",
                    border: "1px solid #fed7d7",
                    borderRadius: 8,
                    padding: 10,
                  }}
                >
                  {err}
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  style={{
                    padding: "12px 18px",
                    borderRadius: 10,
                    border: "none",
                    background: canSubmit ? "#1976d2" : "#9db7e0",
                    color: "#fff",
                    fontWeight: 800,
                    boxShadow: "0 2px 6px rgba(25,118,210,.25)",
                    cursor: canSubmit ? "pointer" : "not-allowed",
                  }}
                  title="送信します"
                >
                  {loading ? "送信中…" : "送信"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setEmail("");
                    setText("");
                    setErr("");
                  }}
                  disabled={loading}
                  style={{
                    padding: "12px 18px",
                    borderRadius: 10,
                    border: "1px solid #d0dae6",
                    background: "#ffffff",
                    color: "#1f2d3d",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  クリア
                </button>
              </div>

              {/* ▼ 送信・クリアの下／同じ枠内：LINE 問い合わせ（object + リンク） */}
              <div
                style={{
                  marginTop: 20,
                  padding: 12,
                  border: "1px dashed #c8d3e3",
                  borderRadius: 10,
                  textAlign: "center",
                  background: "#f9fbff",
                }}
              >
                <h3
                  style={{
                    color: "#222",
                    fontSize: 16,
                    fontWeight: 800,
                    margin: "0 0 10px",
                  }}
                >
                  LINEでお問い合わせ
                </h3>

                <object
                  type="image/png"
                  data={LINE_QR_URL}
                  style={{
                    width: 180,
                    height: 180,
                    maxWidth: "100%",
                    borderRadius: 8,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    background: "#fff",
                    display: "inline-block",
                  }}
                >
                  <div
                    style={{
                      padding: 8,
                      fontSize: 13,
                      color: "#333",
                    }}
                  >
                    QR画像を表示できませんでした。下のリンクから開いてください。
                  </div>
                </object>

                <div style={{ marginTop: 10 }}>
                  <a
                    href={LINE_QR_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: 700,
                    }}
                  >
                    QRコードを開く（新しいタブ）
                  </a>
                </div>

                <div style={{ marginTop: 8 }}>
                  <a
                    href={LINE_ADD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      padding: "10px 14px",
                      background: "#06C755",
                      color: "#fff",
                      borderRadius: 8,
                      fontWeight: 800,
                    }}
                  >
                    LINEを開く
                  </a>
                </div>
              </div>
              {/* ▲ ここまで */}
            </form>
          </div>
        )}
      </div>

      <Footer />
    </>
  );
}
